import type { ResolvedAuthProviderDefinition } from './resolve';
import * as z from 'zod/v4-mini';
import { AuthProtocol } from '../../types/auth-providers/define';

/**
 * Per-provider branding overrides shared across every wire shape. `null` clears
 * the stored override; omitting leaves it untouched. The fallback when all are
 * absent is the preset's registry definition.
 */
export const WireAuthProviderBrandingSchema = z.object({
	displayName: z.optional(z.nullable(z.string())),
	icon: z.optional(z.nullable(z.string())),
	iconColor: z.optional(z.nullable(z.string())),
});

export type WireAuthProviderBranding = z.infer<typeof WireAuthProviderBrandingSchema>;

const branding = WireAuthProviderBrandingSchema.def.shape;
const AuthProtocolSchema = z.enum([AuthProtocol.Password, AuthProtocol.OAuth2]);
const ConfigSchema = z.record(z.string(), z.unknown());

/**
 * `GET /api/auth/providers` — drives login page button rendering. Validated at
 * runtime because the cached payload can be stale or malformed; `protocol` is a
 * free `string` so unknown providers still render.
 */
export const WireAuthProviderListEntrySchema = z.object({
	...branding,
	apiName: z.string(),
	protocol: z.string(),
	preset: z.optional(z.nullable(z.string())),
	loginPath: z.string(),
	loginMethod: z.enum(['GET', 'POST']),
	signupPath: z.string(),
	signupMethod: z.enum(['GET', 'POST']),
});

export const WireAuthProviderListResponseSchema = z.object({
	data: z.array(z.unknown()),
});

export type WireAuthProviderListEntry = z.infer<typeof WireAuthProviderListEntrySchema>;

/**
 * `GET /api/system/auth-providers`. `clientSecret` is write-only and never
 * returned (encrypted at rest).
 */
export const WireAuthProviderAdminRecordSchema = z.object({
	...branding,
	id: z.string(),
	apiName: z.string(),
	preset: z.nullable(z.string()),
	state: z.object({ kind: z.enum(['active', 'disabled']) }),
	protocol: AuthProtocolSchema,
	config: z.optional(ConfigSchema),
});

export type WireAuthProviderAdminRecord = z.infer<typeof WireAuthProviderAdminRecordSchema>;

/** Body for `POST /api/system/auth-providers`. */
export const WireAuthProviderCreateBodySchema = z.object({
	...branding,
	apiName: z.string(),
	protocol: AuthProtocolSchema,
	enabled: z.optional(z.boolean()),
	preset: z.optional(z.nullable(z.string())),
	config: ConfigSchema,
});

export type WireAuthProviderCreateBody = z.infer<typeof WireAuthProviderCreateBodySchema>;

/**
 * `PATCH /api/system/auth-providers/:id`. `protocol`/`preset` are immutable
 * post-create. `null` on a branding field clears the admin override; omitting
 * leaves it untouched.
 */
export const WireAuthProviderUpdateBodySchema = z.object({
	...branding,
	apiName: z.optional(z.string()),
	enabled: z.optional(z.boolean()),
	config: z.optional(ConfigSchema),
});

export type WireAuthProviderUpdateBody = z.infer<typeof WireAuthProviderUpdateBodySchema>;

/**
 * Slice of a `directus_auth_links` row we actually consume. Extend together
 * with the field selection in `useAccountConnections` if more fields needed.
 */
export interface WireAuthLink {
	providerId: string;
}

/**
 * Branding + identity fields shared by both body builders. Branding values are
 * resolved to `string | null` (not optional) by the caller before mapping.
 */
interface AuthProviderBodyFields {
	apiName: string;
	enabled: boolean;
	displayName: string | null;
	icon: string | null;
	iconColor: string | null;
	config: Record<string, unknown>;
}

/**
 * Build the create body from a resolved definition + admin envelope fields.
 * Single source of truth for the registry → BE wire mapping.
 */
export function buildCreateBody(opts: AuthProviderBodyFields & {
	resolved: Pick<ResolvedAuthProviderDefinition, 'protocol' | 'preset'>;
}): WireAuthProviderCreateBody {
	return {
		apiName: opts.apiName,
		protocol: opts.resolved.protocol,
		enabled: opts.enabled,
		preset: opts.resolved.preset,
		displayName: opts.displayName,
		icon: opts.icon,
		iconColor: opts.iconColor,
		config: opts.config,
	};
}

/**
 * Build the update body. Pass-through: callers strip write-only fields (e.g.
 * blank `clientSecret`) before invoking. Send `null` to clear a branding
 * override; omit to leave untouched.
 */
export function buildUpdateBody(opts: AuthProviderBodyFields): WireAuthProviderUpdateBody {
	return {
		apiName: opts.apiName,
		enabled: opts.enabled,
		displayName: opts.displayName,
		icon: opts.icon,
		iconColor: opts.iconColor,
		config: opts.config,
	};
}
